/**
 * BusinessCardManagementPage — admin surface for the public card.
 *
 * Tabs: Waitlist · CAPs · Pitch · Download
 * Route: /admin/business-cards (admin only)
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, Layers, Video, Download, ChevronDown, ChevronUp, Mail, Phone, MapPin, AtSign, Gift, MessageSquare, Save, Trash2, Plus, Loader2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { usePageHeader } from '../context/PageHeaderContext';

const TABS = [
  { id: 'waitlist', label: 'Waitlist', Icon: Users },
  { id: 'caps', label: 'CAPs', Icon: Layers },
  { id: 'pitch', label: 'Pitch', Icon: Video },
  { id: 'download', label: 'Download', Icon: Download },
];

// ────────────── Waitlist tab ──────────────

const WaitlistRow = ({ entry }) => {
  const [open, setOpen] = useState(false);
  const fullName = `${entry.first_name || ''} ${entry.last_name || ''}`.trim() || entry.email || '(no name)';
  const optedMerch = entry.opt_in_merch || entry.merch_drawing || entry.opted_into_merch_draw;
  const optedSms = entry.opt_in_sms || entry.sms_opt_in || entry.opted_into_sms;
  return (
    <div className="bg-white dark:bg-[#1a1a1a] rounded-xl border border-gray-200 dark:border-[#2a2a2a] overflow-hidden" data-testid={`waitlist-row-${entry.id || entry.email}`}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-[#222] text-left"
      >
        <div className="w-10 h-10 rounded-full bg-[#586D52]/15 text-[#586D52] flex items-center justify-center font-semibold text-sm flex-shrink-0">
          {(entry.first_name?.[0] || '').toUpperCase()}{(entry.last_name?.[0] || '').toUpperCase() || ''}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold truncate">{fullName}</p>
          <p className="text-xs text-muted-foreground truncate">{entry.email || entry.phone_number || ''}</p>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {optedMerch && (
            <span title="Opted into free merch draw" className="w-6 h-6 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center" data-testid="waitlist-opt-merch">
              <Gift className="w-3 h-3" />
            </span>
          )}
          {optedSms && (
            <span title="Opted into SMS" className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center" data-testid="waitlist-opt-sms">
              <MessageSquare className="w-3 h-3" />
            </span>
          )}
          {open ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
        </div>
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 border-t border-gray-100 dark:border-[#2a2a2a] grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
          {entry.email && (<div className="flex items-start gap-2"><Mail className="w-4 h-4 text-muted-foreground mt-0.5" /><span className="break-all">{entry.email}</span></div>)}
          {entry.phone_number && (<div className="flex items-start gap-2"><Phone className="w-4 h-4 text-muted-foreground mt-0.5" /><span>{entry.phone_number}</span></div>)}
          {entry.address && (<div className="flex items-start gap-2 md:col-span-2"><MapPin className="w-4 h-4 text-muted-foreground mt-0.5" /><span>{entry.address}</span></div>)}
          {(entry.social_handles || entry.socials) && (
            <div className="flex items-start gap-2 md:col-span-2"><AtSign className="w-4 h-4 text-muted-foreground mt-0.5" />
              <span className="break-all">{typeof entry.social_handles === 'object' ? Object.entries(entry.social_handles).map(([k, v]) => `${k}: ${v}`).join(' · ') : entry.socials}</span>
            </div>
          )}
          <div className="md:col-span-2 text-xs text-muted-foreground pt-1">Submitted {entry.submitted_at ? new Date(entry.submitted_at).toLocaleString() : '—'}</div>
        </div>
      )}
    </div>
  );
};

// ────────────── CAPs tab ──────────────

const CapsEditor = ({ caps, setCaps }) => {
  const updateField = (idx, field, value) => {
    setCaps((prev) => prev.map((c, i) => (i === idx ? { ...c, [field]: value } : c)));
  };
  const remove = (idx) => setCaps((prev) => prev.filter((_, i) => i !== idx));
  const addEmpty = () => setCaps((prev) => [...prev, { title: '', body: '', example_header: '', example_body: '' }]);
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{caps.length} of 15 CAPs · save requires exactly 15 with all four fields filled.</p>
        <Button variant="outline" size="sm" onClick={addEmpty} disabled={caps.length >= 20} data-testid="caps-add-btn"><Plus className="w-4 h-4 mr-1" />Add</Button>
      </div>
      {caps.map((c, idx) => (
        <div key={idx} className="bg-white dark:bg-[#1a1a1a] rounded-xl border border-gray-200 dark:border-[#2a2a2a] p-4 space-y-3" data-testid={`caps-row-${idx}`}>
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">#{idx + 1}</span>
            <button onClick={() => remove(idx)} className="text-muted-foreground hover:text-red-600" aria-label="Remove cap" data-testid={`caps-remove-${idx}`}><Trash2 className="w-4 h-4" /></button>
          </div>
          <div>
            <Label className="text-xs">Title</Label>
            <Input value={c.title} onChange={(e) => updateField(idx, 'title', e.target.value)} data-testid={`caps-title-${idx}`} />
          </div>
          <div>
            <Label className="text-xs">Description (Body)</Label>
            <Textarea rows={4} value={c.body} onChange={(e) => updateField(idx, 'body', e.target.value)} data-testid={`caps-body-${idx}`} />
          </div>
          <div>
            <Label className="text-xs">Example Header</Label>
            <Input value={c.example_header} onChange={(e) => updateField(idx, 'example_header', e.target.value)} data-testid={`caps-eh-${idx}`} />
          </div>
          <div>
            <Label className="text-xs">Example Body</Label>
            <Textarea rows={5} value={c.example_body} onChange={(e) => updateField(idx, 'example_body', e.target.value)} data-testid={`caps-eb-${idx}`} />
          </div>
        </div>
      ))}
    </div>
  );
};

// ────────────── Pitch tab ──────────────

const PitchEditor = ({ pitchState, setPitchState, videoUrl, setVideoUrl }) => (
  <div className="space-y-6">
    <div>
      <Label className="text-sm font-semibold">Pitch State</Label>
      <p className="text-xs text-muted-foreground mb-2">Controls how "Watch the Pitch" behaves on every business card.</p>
      <div className="inline-flex rounded-full border border-gray-200 dark:border-[#2a2a2a] p-1 bg-white dark:bg-[#1a1a1a]" data-testid="pitch-state-toggle">
        {[
          { id: 'normal', label: 'Normal' },
          { id: 'inactive', label: 'Inactive' },
          { id: 'disabled', label: 'Disabled' },
        ].map((s) => (
          <button
            key={s.id}
            onClick={() => setPitchState(s.id)}
            className={`px-4 py-1.5 text-xs font-semibold rounded-full transition-colors ${pitchState === s.id ? 'bg-[#586D52] text-white' : 'text-gray-700 dark:text-gray-300'}`}
            data-testid={`pitch-state-${s.id}`}
          >
            {s.label}
          </button>
        ))}
      </div>
      <ul className="mt-3 text-xs text-muted-foreground space-y-1">
        <li><strong>Normal</strong> — button shows & video autoplays as today.</li>
        <li><strong>Inactive</strong> — button shows; tap → "coming soon" popup → auto-redirect to waitlist after 5s (X to dismiss earlier).</li>
        <li><strong>Disabled</strong> — button hidden entirely from the card.</li>
        <li className="pt-1">In <em>Inactive</em> & <em>Disabled</em> states, the "Learn about ORB" email pill routes recipients to the CAPs popup instead of the pitch popup.</li>
      </ul>
    </div>
    <div>
      <Label className="text-sm font-semibold">Pitch video URL</Label>
      <p className="text-xs text-muted-foreground mb-2">Replace the current video by pasting a new public MP4 URL here.</p>
      <Input value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} placeholder="https://…/pitch.mp4" data-testid="pitch-video-url" />
      {videoUrl && (
        <video src={videoUrl} controls className="mt-3 w-full max-w-md rounded-lg bg-black" preload="metadata" data-testid="pitch-video-preview" />
      )}
    </div>
  </div>
);

// ────────────── Download tab ──────────────

const DownloadEditor = ({ url, setUrl }) => (
  <div className="space-y-4 max-w-xl">
    <div>
      <Label className="text-sm font-semibold">ORB download URL</Label>
      <p className="text-xs text-muted-foreground mb-2">Paste the App Store / Play Store / web download link. The user's referral ID is appended automatically as <code>?ref=&lt;id&gt;</code> on every card.</p>
      <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://apps.apple.com/…/orb" data-testid="download-url-input" />
    </div>
    {url && (
      <div className="text-xs text-muted-foreground">
        Preview as someone with referral ID <code>abc12345</code>: <span className="break-all"><a className="text-[#586D52] underline" href={url + (url.includes('?') ? '&' : '?') + 'ref=abc12345'} target="_blank" rel="noreferrer">{url + (url.includes('?') ? '&' : '?') + 'ref=abc12345'}</a></span>
      </div>
    )}
  </div>
);

// ────────────── Page ──────────────

const BusinessCardManagementPage = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('waitlist');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [waitlist, setWaitlist] = useState([]);
  const [caps, setCaps] = useState([]);
  const [pitchState, setPitchState] = useState('normal');
  const [videoUrl, setVideoUrl] = useState('');
  const [downloadUrl, setDownloadUrl] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [s, w] = await Promise.all([
          api.get('/admin/business-cards/settings'),
          api.get('/admin/business-cards/waitlist'),
        ]);
        if (cancelled) return;
        setCaps(s.data.caps || []);
        setPitchState(s.data.pitch_state || 'normal');
        setVideoUrl(s.data.pitch_video_url || '');
        setDownloadUrl(s.data.download_url || '');
        setWaitlist(w.data.items || []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Failed to load settings');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [api]);

  const capsValid = useMemo(() => caps.length === 15 && caps.every((c) => c.title?.trim() && c.body?.trim() && c.example_header?.trim() && c.example_body?.trim()), [caps]);

  const save = async () => {
    if (!capsValid) {
      toast.error(`Save blocked — need exactly 15 CAPs with all fields filled. Currently ${caps.length} CAPs; check for empty titles/descriptions.`);
      setActiveTab('caps');
      return;
    }
    setSaving(true);
    try {
      await api.put('/admin/business-cards/settings', {
        pitch_state: pitchState,
        pitch_video_url: videoUrl,
        download_url: downloadUrl,
        caps,
      });
      toast.success('Saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // Standardize on the global page header (matches every other
  // admin page: title in the global header, back button, optional
  // right slot for the Save action).
  usePageHeader({
    title: 'Manage Business Cards',
    back: '/business-card',
    rightSlot: activeTab !== 'waitlist' ? (
      <Button onClick={save} disabled={saving || loading} size="sm" className="bg-[#586D52] hover:bg-[#4a5d46] text-white" data-testid="bcm-save">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" />Save</>}
      </Button>
    ) : null,
  }, [activeTab, saving, loading, capsValid, caps, pitchState, videoUrl, downloadUrl]);

  if (user?.role !== 'admin') {
    return <div className="p-8 text-center"><p>Admin only.</p></div>;
  }

  return (
    <div className="max-w-4xl mx-auto px-4 pb-24" data-testid="business-card-mgmt-page">
      <div className="flex gap-2 overflow-x-auto whitespace-nowrap py-3 mb-4 border-b" data-testid="bcm-tabs">
        {TABS.map(({ id, label, Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`px-3 py-1.5 text-xs font-semibold rounded-full border transition-colors flex-shrink-0 inline-flex items-center gap-1.5 ${activeTab === id ? 'bg-[#586D52] text-white border-[#586D52]' : 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200 dark:bg-[#1f1f1f] dark:text-gray-300 dark:border-[#2a2a2a]'}`}
            data-testid={`bcm-tab-${id}`}
          >
            <Icon className="w-3.5 h-3.5" /> {label}
          </button>
        ))}
      </div>
      {loading && (<div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>)}
      {!loading && activeTab === 'waitlist' && (
        waitlist.length === 0 ? (
          <p className="text-center text-muted-foreground py-10">No waitlist signups yet.</p>
        ) : (
          <div className="space-y-2" data-testid="bcm-waitlist-list">{waitlist.map((w, i) => <WaitlistRow key={w.id || i} entry={w} />)}</div>
        )
      )}
      {!loading && activeTab === 'caps' && (<CapsEditor caps={caps} setCaps={setCaps} />)}
      {!loading && activeTab === 'pitch' && (<PitchEditor pitchState={pitchState} setPitchState={setPitchState} videoUrl={videoUrl} setVideoUrl={setVideoUrl} />)}
      {!loading && activeTab === 'download' && (<DownloadEditor url={downloadUrl} setUrl={setDownloadUrl} />)}
    </div>
  );
};

export default BusinessCardManagementPage;
